/**
 * Phase 7 — AMC Monthly Confirmations tab (amc_recurring WO detail).
 * SLA verification, penalty on breach, invoice raising.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Plus } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR } from '../../lib/format';

const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';
const CONF_BADGE = {
  pending: 'bg-slate-100 text-slate-600', confirmed: 'bg-blue-50 text-blue-700',
  invoiced: 'bg-emerald-50 text-emerald-700', paid: 'bg-emerald-100 text-emerald-800',
};

export default function WOAmcTab({ wo, onChanged }) {
  const [confs, setConfs] = useState(null);
  const [confirming, setConfirming] = useState(null);   // conf being confirmed
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet('/amc-confirmations', { work_order_id: wo.id }).then((d) => setConfs(d?.items || [])).catch(() => setConfs([]));
  }, [wo.id]);
  useEffect(() => { load(); }, [load]);

  const createMonth = async () => {
    const month = window.prompt('Month (YYYY-MM):', new Date().toISOString().slice(0, 7));
    if (!month) return;
    setBusy(true);
    try {
      await apiPost('/amc-confirmations', { work_order_id: wo.id, month });
      toast.success(`Confirmation cycle created for ${month}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const raiseInvoice = async (c) => {
    setBusy(true);
    try {
      const inv = await apiPost(`/amc-confirmations/${c.id}/raise-invoice`, {});
      toast.success(`${inv.invoice_no} registered in Vendor Invoices`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (confs === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="wo-amc-tab">
      <div className="flex items-center justify-between">
        <div className="kpi-overline flex items-center gap-1.5"><RefreshCw size={11} /> Monthly Service Confirmations ({confs.length})</div>
        <button onClick={createMonth} disabled={busy} className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="amc-create-month-btn">
          <Plus size={11} /> New Month
        </button>
      </div>
      <p className="text-[11px] text-slate-500">Pending cycles are also auto-created on the 25th of each month for active AMCs. Expected: {formatINR(wo.amc_monthly_value)} / month · SLA: {wo.sla_terms || '—'}</p>

      <table className="w-full dense-table">
        <thead><tr><th>Month</th><th>SLA Met</th><th className="text-right">Penalty</th><th className="text-right">Payable</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {confs.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400 text-xs">No confirmation cycles yet</td></tr>}
          {confs.map((c) => (
            <tr key={c.id} data-testid={`amc-conf-${c.month}`}>
              <td className="font-mono text-xs">{c.month}</td>
              <td className="text-xs">{c.sla_met === null ? '—' : c.sla_met ? <span className="text-emerald-600 font-bold">Yes</span> : <span className="text-red-600 font-bold">No</span>}</td>
              <td className="text-right tabular-nums text-xs text-red-600">{c.penalty_amount ? formatINR(c.penalty_amount) : '—'}</td>
              <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(c.invoice_amount_payable)}</td>
              <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${CONF_BADGE[c.status] || ''}`}>{c.status}</span></td>
              <td>
                <div className="flex gap-1 justify-end">
                  {c.status === 'pending' && (
                    <button onClick={() => setConfirming(c)} className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid={`amc-confirm-btn-${c.month}`}>Confirm Service</button>
                  )}
                  {c.status === 'confirmed' && (
                    <button onClick={() => raiseInvoice(c)} disabled={busy} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`amc-invoice-btn-${c.month}`}>Raise Invoice</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {confirming && <ConfirmModal conf={confirming} onClose={(saved) => { setConfirming(null); if (saved) { load(); onChanged?.(); } }} />}
    </div>
  );
}

function ConfirmModal({ conf, onClose }) {
  const [slaItems, setSlaItems] = useState(
    (conf.sla_items?.length ? conf.sla_items : [{ description: '', target_value: '', actual_value: '' }])
      .map((it) => ({ ...it }))
  );
  const [penaltyPct, setPenaltyPct] = useState('10');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiPost(`/amc-confirmations/${conf.id}/confirm`, {
        sla_items: slaItems.filter((it) => it.description).map((it) => ({
          description: it.description,
          target_value: Number(it.target_value) || 0,
          actual_value: Number(it.actual_value) || 0,
        })),
        penalty_pct: Number(penaltyPct) || 0,
        notes,
      });
      toast.success('Service confirmed');
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="amc-confirm-modal">
      <div className="bg-white rounded-sm border border-slate-200 w-full max-w-lg p-4 space-y-3">
        <h3 className="font-display font-bold text-sm">Confirm Service — {conf.month}</h3>
        <div className="kpi-overline">SLA Items (actual ≥ target = met)</div>
        {slaItems.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input placeholder="SLA description" value={it.description} onChange={(e) => setSlaItems(slaItems.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} className={`${inp} flex-1`} data-testid={`amc-sla-desc-${i}`} />
            <input placeholder="Target" type="number" value={it.target_value} onChange={(e) => setSlaItems(slaItems.map((x, j) => j === i ? { ...x, target_value: e.target.value } : x))} className={`${inp} w-20`} data-testid={`amc-sla-target-${i}`} />
            <input placeholder="Actual" type="number" value={it.actual_value} onChange={(e) => setSlaItems(slaItems.map((x, j) => j === i ? { ...x, actual_value: e.target.value } : x))} className={`${inp} w-20`} data-testid={`amc-sla-actual-${i}`} />
            <button onClick={() => setSlaItems(slaItems.filter((_, j) => j !== i))} className="text-xs text-red-500 px-1">✕</button>
          </div>
        ))}
        <button onClick={() => setSlaItems([...slaItems, { description: '', target_value: '', actual_value: '' }])} className="text-xs text-[#3b4872] font-display font-bold">+ Add SLA item</button>
        <label className="text-[10px] text-slate-500 block">Penalty % if SLA missed
          <input type="number" value={penaltyPct} onChange={(e) => setPenaltyPct(e.target.value)} className={`${inp} mt-0.5 max-w-[120px]`} data-testid="amc-penalty-pct" /></label>
        <textarea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inp} h-14`} />
        <div className="flex gap-2 pt-1">
          <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="amc-confirm-save">{saving ? 'Saving…' : 'Confirm'}</button>
          <button onClick={() => onClose(false)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
